import { useNavigate } from "react-router-dom";
import { X, Plus, Minus, Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { fmtEUR } from "@/lib/api";
import { useT } from "@/context/I18nContext";

const FREE_SHIPPING_THRESHOLD = 35;
const MIN_ORDER = 15;

export default function CartDrawer() {
  const { open, setOpen, items, update, remove, subtotal } = useCart();
  const { t } = useT();
  const nav = useNavigate();

  const progress = Math.min(100, (subtotal / FREE_SHIPPING_THRESHOLD) * 100);
  const meetsMin = subtotal >= MIN_ORDER;

  return (
    <>
      {open && <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setOpen(false)} data-testid="cart-backdrop" />}
      <aside className={`fixed inset-y-0 right-0 w-full sm:w-[420px] bg-[color:var(--brunchi-cream)] z-50 shadow-2xl transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"} flex flex-col`} data-testid="cart-drawer">
        <div className="flex items-center justify-between px-6 py-5 border-b border-[color:var(--brunchi-stone)]">
          <h2 className="font-serif text-2xl">{t("cart.title")}</h2>
          <button onClick={() => setOpen(false)} className="p-2 hover:bg-[color:var(--brunchi-cream-2)] rounded-full" data-testid="cart-close-btn">
            <X size={20} />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center gap-3">
            <ShoppingBag size={48} className="text-[color:var(--brunchi-stone-2)]" />
            <div className="font-serif text-xl">{t("cart.empty_title")}</div>
            <p className="text-sm text-[color:var(--brunchi-ink-soft)]">{t("cart.empty_body")}</p>
            <button onClick={() => { setOpen(false); nav("/shop"); }}
              className="mt-2 px-6 py-3 rounded-full pill-primary text-sm font-medium" data-testid="cart-shop-btn">
              {t("cart.browse")}
            </button>
          </div>
        ) : (
          <>
            <div className="px-6 py-4 border-b border-[color:var(--brunchi-stone)] bg-[color:var(--brunchi-cream-2)]">
              <div className="text-xs uppercase tracking-widest text-[color:var(--brunchi-forest)] font-medium mb-1.5">
                {subtotal >= FREE_SHIPPING_THRESHOLD
                  ? t("cart.free_unlocked")
                  : `${fmtEUR(FREE_SHIPPING_THRESHOLD - subtotal)} ${t("cart.free_progress")}`}
              </div>
              <div className="h-1.5 bg-[color:var(--brunchi-stone)] rounded-full overflow-hidden">
                <div className="h-full bg-[color:var(--brunchi-terracotta)]" style={{ width: `${progress}%` }} />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" data-testid="cart-items">
              {items.map((it) => (
                <div key={it.product_id} className="flex gap-3" data-testid={`cart-item-${it.slug}`}>
                  {it.image && <img src={it.image} alt="" className="w-20 h-20 object-cover rounded-lg" />}
                  <div className="flex-1">
                    <div className="font-medium text-sm">{it.name}</div>
                    <div className="text-xs font-mono text-[color:var(--brunchi-ink-soft)]">{fmtEUR(it.price_eur)}</div>
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={() => update(it.product_id, it.quantity - 1)}
                        className="w-7 h-7 rounded-full border border-[color:var(--brunchi-stone-2)] flex items-center justify-center hover:bg-[color:var(--brunchi-cream-2)]"
                        data-testid={`cart-decrease-${it.slug}`}><Minus size={12} /></button>
                      <span className="font-mono text-sm w-5 text-center" data-testid={`cart-qty-${it.slug}`}>{it.quantity}</span>
                      <button onClick={() => update(it.product_id, it.quantity + 1)}
                        className="w-7 h-7 rounded-full border border-[color:var(--brunchi-stone-2)] flex items-center justify-center hover:bg-[color:var(--brunchi-cream-2)]"
                        data-testid={`cart-increase-${it.slug}`}><Plus size={12} /></button>
                      <button onClick={() => remove(it.product_id)}
                        className="ml-auto p-1.5 text-[color:var(--brunchi-error)] hover:bg-red-50 rounded"
                        data-testid={`cart-remove-${it.slug}`}><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <div className="font-mono text-sm">{fmtEUR(it.price_eur * it.quantity)}</div>
                </div>
              ))}
            </div>

            <div className="px-6 py-5 border-t border-[color:var(--brunchi-stone)] space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-[color:var(--brunchi-ink-soft)]">{t("cart.subtotal")}</span>
                <span className="font-mono font-semibold" data-testid="cart-subtotal">{fmtEUR(subtotal)}</span>
              </div>
              {!meetsMin && (
                <div className="text-xs px-3 py-2 rounded-md bg-[color:var(--brunchi-terracotta-light)] text-[color:var(--brunchi-terracotta)]">
                  {t("cart.min_order_prefix")} {fmtEUR(MIN_ORDER)} — {t("cart.min_order_add")} {fmtEUR(MIN_ORDER - subtotal)} {t("cart.min_order_more")}
                </div>
              )}
              <button disabled={!meetsMin}
                onClick={() => { setOpen(false); nav("/checkout"); }}
                className="w-full py-3 rounded-full pill-primary font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="cart-checkout-btn">
                {t("cart.checkout")}
              </button>
            </div>
          </>
        )}
      </aside>
    </>
  );
}
